import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { useBooking } from "../context/BookingContext";
import { IMAGES, SERVICES, WHY_US, CONDITIONS, CONTACT, SETTINGS } from "../data/content";

const RegionPill = ({ r }) => (
  <span data-testid={`region-${r}`} className="inline-flex items-center gap-2 rounded-full bg-sora-warm border border-sora-stone/40 px-4 py-2 text-sm text-sora-slate">
    <MapPin size={14} strokeWidth={1.5} className="text-sora-blue" /> {r}
  </span>
);

export default function Home() {
  const { openBooking } = useBooking();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 sora-grain opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-sora-sage/40 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-sora-slate font-bold mb-6">
              Mobile paediatric speech pathology
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-sora-slate tracking-tight">
              Helping children find their voice with confidence.
            </h1>
            <p className="mt-6 text-lg text-sora-charcoal/75 leading-relaxed max-w-md">
              Compassionate, evidence-based speech pathology for children and families — delivered in the places your child already loves.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button data-testid="hero-book" onClick={() => openBooking("booking")} className="inline-flex items-center gap-2 bg-sora-blue text-white rounded-[20px] px-8 py-4 font-bold hover:bg-sora-slate transition-colors shadow-sm">
                Book an Appointment <ArrowRight size={18} />
              </button>
              <Link data-testid="hero-learn" to="/services" className="inline-flex items-center gap-2 bg-sora-warm text-sora-slate border border-sora-stone/50 rounded-[20px] px-8 py-4 font-bold hover:bg-white transition-colors">
                Learn More
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="relative">
            <div className="rounded-[28px] overflow-hidden shadow-[0_30px_80px_rgba(63,88,115,0.15)]">
              <img data-testid="hero-image" src={IMAGES.hero} alt="Speech pathologist playing with a child using wooden toys" className="w-full h-auto object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-sora-warm rounded-[22px] shadow-[0_16px_40px_rgba(63,88,115,0.12)] px-6 py-4">
              <p className="font-display text-2xl text-sora-slate">Metro Melbourne</p>
              <p className="text-xs uppercase tracking-[0.18em] text-sora-blue mt-1">Therapy that comes to you</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MOBILE SERVICE CALLOUT */}
      <section className="py-16 bg-sora-slate text-sora-warm">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] text-sora-sage font-bold mb-4">Service Area</p>
            <h2 className="font-display text-3xl sm:text-4xl leading-snug max-w-3xl mx-auto">{CONTACT.tagline}</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {CONTACT.regions.map((r) => (
                <span key={r} className="rounded-full border border-sora-warm/25 px-5 py-2.5 text-sm text-sora-warm/90">{r}</span>
              ))}
            </div>
            <p className="mt-5 text-sm text-sora-warm/60">…and most of metropolitan Melbourne.</p>
          </Reveal>
        </div>
      </section>

      {/* WELCOME */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-[28px] overflow-hidden shadow-[0_20px_60px_rgba(63,88,115,0.1)]">
              <img src={IMAGES.babyBlocks} alt="Child at play with natural wooden toys" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Welcome to Sora Speech Co.</p>
            <h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug">Thoughtful care for every child and family.</h2>
            <p className="mt-5 text-sora-charcoal/75 leading-relaxed">
              Sora Speech Co. is a mobile paediatric speech pathology practice built around your family. We believe children flourish when therapy feels natural, playful, and connected to everyday life.
            </p>
            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              {["Family-centred", "Neurodiversity affirming", "Strengths-based", "Evidence-based"].map((t) => (
                <div key={t} className="flex items-center gap-3 rounded-[18px] bg-sora-warm border border-sora-stone/30 px-5 py-4">
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-sora-sage/50 text-sora-slate"><Check size={16} /></span>
                  <span className="text-sora-slate font-medium">{t}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-sora-warm/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Our Services</p>
            <h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug">Support tailored to your child's unique journey.</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div data-testid={`service-card-${i}`} className="group h-full rounded-[24px] bg-sora-warm border border-sora-stone/25 p-8 shadow-[0_8px_30px_rgba(63,88,115,0.05)] hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(63,88,115,0.1)] transition-all duration-300">
                  <span className="grid place-items-center h-14 w-14 rounded-full bg-sora-blue/12 text-sora-blue group-hover:bg-sora-blue group-hover:text-white transition-colors duration-300">
                    <Icon name={s.icon} size={26} />
                  </span>
                  <h3 className="font-display text-2xl text-sora-slate mt-6">{s.title}</h3>
                  <p className="mt-3 text-sora-charcoal/75 leading-relaxed">{s.desc}</p>
                  <Link to="/services" className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-sora-blue group-hover:gap-2.5 transition-all">
                    Explore <ArrowRight size={15} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY FAMILIES CHOOSE US */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Why Families Choose Us</p>
            <h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug">Thoughtful care, wherever your child feels most at home.</h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_US.map((w, i) => (
              <Reveal key={w.text} delay={i * 0.05}>
                <div className="flex items-center gap-4 rounded-[20px] bg-sora-warm border border-sora-stone/25 px-6 py-5 h-full">
                  <span className="shrink-0 grid place-items-center h-11 w-11 rounded-full bg-sora-sage/45 text-sora-slate"><Icon name={w.icon} size={20} /></span>
                  <span className="text-sora-slate font-medium">{w.text}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SETTINGS STRIP */}
      <section className="py-20 bg-sora-sage/25">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Where We Work</p>
            <h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug">No clinic — just therapy woven into everyday life.</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SETTINGS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div className="rounded-[22px] bg-sora-warm p-7 h-full border border-sora-stone/25">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-sora-blue/12 text-sora-blue"><Icon name={s.icon} size={22} /></span>
                  <h3 className="font-display text-xl text-sora-slate mt-4">{s.title}</h3>
                  <p className="mt-2 text-sm text-sora-charcoal/75 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE SUPPORTING */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Experience Supporting</p>
            <h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug">Trusted experience across a breadth of needs.</h2>
          </Reveal>
          <div className="mt-12 grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {CONDITIONS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.04}>
                <div className="flex flex-col items-center text-center rounded-[20px] bg-sora-warm border border-sora-stone/25 px-4 py-7 h-full hover:bg-sora-sage/20 transition-colors">
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-sora-sage/45 text-sora-slate"><Icon name={c.icon} size={22} /></span>
                  <span className="mt-4 text-sm font-medium text-sora-slate">{c.title}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA />
    </div>
  );
}

export const CTA = () => {
  const { openBooking } = useBooking();
  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-sora-blue to-sora-slate px-8 py-16 md:p-20 text-center">
            <div className="absolute inset-0 sora-grain opacity-20 pointer-events-none" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-5xl text-white leading-tight max-w-2xl mx-auto">Ready to get started?</h2>
              <p className="mt-5 text-lg text-white/85 max-w-xl mx-auto">Book your initial assessment today and take the first step towards confident communication.</p>
              <button data-testid="cta-book" onClick={() => openBooking("booking")} className="mt-9 inline-flex items-center gap-2 bg-sora-warm text-sora-slate rounded-[20px] px-9 py-4 font-bold hover:bg-white transition-colors shadow-lg">
                Book your initial assessment <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
